import Joyride, { CallBackProps, STATUS, Step } from 'react-joyride';
import { useSetState } from 'react-use';
import { Button } from '@material-ui/core';

interface State {
  run: boolean;
  steps: Step[];
}

interface Messages {
  [key: string]: any;
}

const messages: Messages = {
  back: 'Back',
  close: 'Close',
  last: 'Finish',
  next: 'Next',
  open: 'Open the dialog',
  restart: 'Restart the tour',
  skip: 'Close the tour',
  title: 'You can use custom components!',
};

const spotlightPadding = 5;

export default function Tour() {
  const [{ run, steps }, setState] = useSetState<State>({
    run: false,
    steps: [
      {
        content: <h2>Let's begin our journey!</h2>,
        placement: 'center',
        target: 'body',
      },
      {
        content: (
          <div>
            <h2>The search field</h2>
            <p>
              Find the right information you are looking for in our developer
              platform.
            </p>
          </div>
        ),
        spotlightPadding,
        target: '[aria-label="Search"]',
      },
      {
        content: (
          <div>
            <h2>You are here currently: the home page!</h2>
            <p>
              This is your personalized dashboard where you can view an overview
              of starred entities and quickly access to various tools and
              resources.
            </p>
          </div>
        ),
        spotlightPadding,
        target: '[aria-label="Home"][aria-current="page"]',
      },
      {
        content: (
          <div>
            <h2>The software catalog</h2>
            <p>
              It is the centralized system that keeps track of ownership and
              metadata for all the software in our developer platform.
            </p>
          </div>
        ),
        target: '[aria-label="Catalog"]',
      },
      {
        content: (
          <div>
            <h2>See your group</h2>
            <p>
              Here you can view the group you are part of, see relevant
              projects, and collaborate with your team members more effectively.
            </p>
          </div>
        ),
        target: '[aria-label="My Group"]',
      },
      {
        content: (
          <div>
            <h2>APIs overview</h2>
            <p>
              Browse, manage, and view details of APIs, including versions and
              usage.
            </p>
          </div>
        ),
        target: '[aria-label="APIs"]',
      },
      {
        content: (
          <div>
            <h2>Explore documentations!</h2>
            <p>
              Access project documentation, auto-generated and searchable for
              easy reference.
            </p>
          </div>
        ),
        target: '[aria-label="Docs"]',
      },
      {
        content: (
          <div>
            <h2>Create a new project here!</h2>
            <p>
              Automate the setup of new projects and services with templates to
              speed up development.
            </p>
          </div>
        ),
        target: '[aria-label="Create..."]',
      },
      {
        content: (
          <div>
            <h2>Settings</h2>
            <p>Set some preferences here.</p>
          </div>
        ),
        target: '[aria-label="Settings"]',
      },
      {
        content: (
          <div>
            <h2>If you are REALLY interested: the DevTools plugin!</h2>
            <p>
              Lists helpful information about this Backstage instance such as:
              OS, NodeJS version, Backstage version, and package versions.
            </p>
          </div>
        ),
        target: '[aria-label="DevTools"]',
      },
      {
        content: (
          <div>
            <h2>Access your favorites!</h2>
            <p>
              Here you'll find your starred entities, a personalized list of
              your most important or frequently accessed services, APIs, and
              components, making it easy to quickly navigate to the resources
              you care about most.
            </p>
          </div>
        ),
        target: '[aria-label="StarredEntities"]',
      },
      {
        content: (
          <div>
            <h2>See some helpful links here!</h2>
            <p>This a collection of links to our tools and the support page.</p>
          </div>
        ),
        target: '[aria-label="Links"]',
      },
    ],
  });

  const handleClickStart = (event: React.MouseEvent<HTMLElement>) => {
    event.preventDefault();

    setState({
      run: true,
    });
  };

  const handleJoyrideCallback = (data: CallBackProps) => {
    const { status } = data;
    const finishedStatuses: string[] = [STATUS.FINISHED, STATUS.SKIPPED];

    if (finishedStatuses.includes(status)) {
      setState({ run: false });
    }
  };

  return (
    <>
      <Joyride
        callback={handleJoyrideCallback}
        continuous
        run={run}
        scrollToFirstStep
        showProgress
        spotlightPadding={spotlightPadding}
        floaterProps={{ disableAnimation: true }}
        hideCloseButton
        locale={messages}
        showSkipButton
        steps={steps}
        styles={{
          options: {
            zIndex: 10000,
            primaryColor: '#007ac9',
            beaconSize: 12,
          },
        }}
      />
      <Button
        variant="contained"
        color="primary"
        onClick={handleClickStart}
        size="large"
      >
        Start
      </Button>
    </>
  );
}
